import React, { useMemo, useState } from 'react'
import styles from './predefined.module.scss'
import { type BodyParts, type StaticPosition, STATIC_POSITIONS } from './static-positions'
import { Icon } from '@iconify/react'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import { useAppSelector } from 'common/hooks/redux'
import { isV2ModelVersion } from 'features/Procedure/hooks/useEvaluateModel'
import { FloatButton, Button } from 'antd'

function isApproximatelyEqual(a: number, b: number, digits = 2) {
  const factor = Math.pow(10, digits)
  return Math.round(a * factor) === Math.round(b * factor)
}

function hasChildren(
  bodyPart: BodyParts,
): bodyPart is Extract<BodyParts, { children: StaticPosition[] }> {
  return 'children' in bodyPart
}

type Props = {
  isVisible?: boolean
  getPopupContainer?: () => HTMLElement
  hide?: boolean
}

export const PredefinedPositions = React.memo(
  ({ isVisible = true, getPopupContainer, hide }: Props) => {
    const [open, setOpen] = useState(false)
    const { handleSetDesiredCameraPosition } = useSceneActions()
    const scene = useAppSelector((state) => state.procedureReducer.scene)
    const modelVersion = useAppSelector((state) => state.procedureReducer.modelVersion)
    const positionsForVersion = useMemo(
      () => (isV2ModelVersion(modelVersion) ? STATIC_POSITIONS.v2 : STATIC_POSITIONS.v1),
      [modelVersion],
    )

    if (!isVisible) return null

    const handlePositionClick = (position: BodyParts) => {
      if (!hasChildren(position)) {
        handleSetDesiredCameraPosition({
          position: position.position,
          target: position.target,
        })
        setOpen(false)
      }
    }

    const handleChildClick = (child: StaticPosition, e: React.MouseEvent) => {
      e.stopPropagation()
      handleSetDesiredCameraPosition({
        position: child.position,
        target: child.target,
      })
      setOpen(false)
    }

    const renderTooltip = (position: BodyParts) => {
      if (!hasChildren(position)) {
        return null
      }

      const sections = position.children.reduce<
        Array<{ key: string; title?: string; children: StaticPosition[] }>
      >((groups, child) => {
        const key = child.subCategory ?? 'default'
        const existingGroup = groups.find((group) => group.key === key)

        if (existingGroup) {
          existingGroup.children.push(child)
          return groups
        }

        groups.push({
          key,
          title: child.subCategory,
          children: [child],
        })

        return groups
      }, [])

      return (
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipTitle}>{position.name}</div>
          <div className={styles.tooltipSections}>
            {sections.map((section) => (
              <div key={section.key} className={styles.tooltipSection}>
                {section.title ? (
                  <div className={styles.tooltipSectionTitle}>{section.title}</div>
                ) : null}
                <div className={styles.tooltipButtons}>
                  {section.children.map((child) => {
                    const isSelected =
                      isApproximatelyEqual(scene.position.x, child.position.x) &&
                      isApproximatelyEqual(scene.position.y, child.position.y) &&
                      isApproximatelyEqual(scene.position.z, child.position.z)

                    return (
                      <Button
                        key={child.id}
                        type={isSelected ? 'primary' : 'default'}
                        size='small'
                        className={styles.tooltipButton}
                        onClick={(e) => handleChildClick(child, e)}>
                        {child.label ?? child.name}
                      </Button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (hide) return null

    return (
      <FloatButton.Group
        className={styles.floatButtonGroup}
        shape='square'
        open={open}
        trigger='click'
        icon={<Icon icon='healthicons:body-outline' color='white' />}
        onClick={() => setOpen(!open)}>
        {positionsForVersion.map((position) => (
          <FloatButton
            rootClassName={styles.floatButtonRoot}
            key={position.id}
            icon={<Icon icon={position.icon} width={20} height={20} />}
            tooltip={{
              title: renderTooltip(position),
              placement: 'left',
              trigger: 'click',
              overlayClassName: styles.customTooltip,
              getPopupContainer: getPopupContainer,
            }}
            onClick={() => handlePositionClick(position)}
          />
        ))}
      </FloatButton.Group>
    )
  },
)
